// Date utility functions for consistent date formatting across the app

// ─── Singapore Time (SGT = UTC+8) helpers ────────────────────────────────────

const SGT_TIMEZONE = 'Asia/Singapore';

/**
 * Returns true when the browser is NOT effectively on Singapore time.
 *
 * We check BOTH the reported timezone name AND the actual UTC offset, because
 * they can disagree — e.g. a laptop physically in Toronto whose browser still
 * reports 'Asia/Singapore' but whose real offset is Toronto's. SGT is UTC+8,
 * so a device truly on SGT reports getTimezoneOffset() === -480 minutes.
 * If either the name or the offset isn't SGT, we treat it as non-SGT.
 */
export const isNonSGTBrowser = () => {
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetIsSGT = new Date().getTimezoneOffset() === -480; // UTC+8
  return tzName !== SGT_TIMEZONE || !offsetIsSGT;
};

/**
 * Returns today's date as a YYYY-MM-DD string in SGT.
 * Replaces `new Date().toISOString().split('T')[0]` everywhere the portal
 * needs a calendar date (which is always a Singapore date).
 */
export const todaySGT = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: SGT_TIMEZONE });
// en-CA gives YYYY-MM-DD format natively — no manual padding needed.

/**
 * Format a UTC date/ISO string for display as a full date-time in SGT.
 * Equivalent to the existing `en-SG` toLocaleString calls, but with the
 * timezone pinned to Asia/Singapore regardless of the viewer's browser.
 *
 * @param {Date|string} date
 * @param {object}      opts  — extra Intl.DateTimeFormat options to override defaults
 * @returns {string}
 */
export const formatDateTimeSGT = (date, opts = {}) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-SG', {
    timeZone: SGT_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...opts,
  });
};

/**
 * Format a UTC date/ISO string for display as a date-only string in SGT.
 *
 * @param {Date|string} date
 * @param {object}      opts  — extra Intl.DateTimeFormat options to override defaults
 * @returns {string}
 */
export const formatDateSGT = (date, opts = {}) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-SG', {
    timeZone: SGT_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...opts,
  });
};

/**
 * Convert a plain YYYY-MM-DD date string + HH:MM time string that the user
 * typed (always treated as SGT) into a UTC ISO-8601 string suitable for
 * sending to the backend.
 *
 * e.g. dateTimeToSGTIso('2026-09-01', '14:30') → '2026-09-01T06:30:00.000Z'
 *
 * @param {string} dateStr   — YYYY-MM-DD
 * @param {string} timeStr   — HH:MM  (24-hour)
 * @returns {string}         — UTC ISO string
 */
export const dateTimeToSGTIso = (dateStr, timeStr = '12:00') => {
  // Appending '+08:00' makes the Date parser treat the value as SGT
  // regardless of the browser's own timezone.
  return new Date(`${dateStr}T${timeStr}:00+08:00`).toISOString();
};

/**
 * Get the current month and year numbers in SGT (1-indexed month).
 * Replaces `new Date().getMonth() + 1` / `new Date().getFullYear()`.
 * @returns {{ month: number, year: number }}
 */
export const currentMonthYearSGT = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SGT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  return {
    year: parseInt(parts.find(p => p.type === 'year').value, 10),
    month: parseInt(parts.find(p => p.type === 'month').value, 10),
  };
};

/**
 * Same as formatDateSGT but returns a React fragment appending a small
 * "(SGT)" badge when the viewer's browser is outside Singapore.
 * Import React in any component that uses this.
 *
 * @param {Date|string} date
 * @param {object}      opts  — extra Intl.DateTimeFormat options
 * @returns {React.ReactNode}
 */
export const formatDateSGTLabelled = (date, opts = {}) => {
  const text = formatDateSGT(date, opts);
  if (!text) return '';
  if (!isNonSGTBrowser()) return text;
  // Return a plain string with the suffix — callers wrap in JSX
  return text + '\u00A0(SGT)';
};

/**
 * Same as formatDateTimeSGT but appends "(SGT)" for non-SGT browsers.
 *
 * @param {Date|string} date
 * @param {object}      opts  — extra Intl.DateTimeFormat options
 * @returns {string}
 */
export const formatDateTimeSGTLabelled = (date, opts = {}) => {
  const text = formatDateTimeSGT(date, opts);
  if (!text) return '';
  if (!isNonSGTBrowser()) return text;
  return text + '\u00A0(SGT)';
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format date to DD/MM/YYYY format
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string in DD/MM/YYYY format
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Convert date to YYYY-MM-DD format for input[type="date"]
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  return d.toISOString().split('T')[0];
};

/**
 * Calculate age from date of birth
 * @param {Date|string} dateOfBirth - Date of birth
 * @returns {number} Age in years
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  
  if (isNaN(birthDate.getTime())) return null;
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Format date with age display
 * @param {Date|string} dateOfBirth - Date of birth
 * @returns {string} Formatted string like "15/03/1990 (34 years old)"
 */
export const formatDateWithAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';
  
  const formattedDate = formatDateDDMMYYYY(dateOfBirth);
  const age = calculateAge(dateOfBirth);
  
  if (!formattedDate) return '';
  if (age === null) return formattedDate;
  
  return `${formattedDate} (${age} years old)`;
};

/**
 * Validate date is not in the future
 * @param {Date|string} date - Date to validate
 * @returns {boolean} True if date is valid and not in future
 */
export const isValidPastDate = (date) => {
  if (!date) return false;
  
  const d = new Date(date);
  const today = new Date();
  
  return !isNaN(d.getTime()) && d <= today;
};

/**
 * Validate date is within reasonable range (1900 to today)
 * @param {Date|string} date - Date to validate
 * @returns {boolean} True if date is within range
 */
export const isValidDateOfBirth = (date) => {
  if (!date) return false;
  
  const d = new Date(date);
  const minDate = new Date('1900-01-01');
  const today = new Date();
  
  return !isNaN(d.getTime()) && d >= minDate && d <= today;
};
